import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { republishCourse, unpublishCourse } from "@/lib/courseAdmin";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  archiveCourse,
  addChapter,
  aiAppendChapter,
  aiGenerationStatus,
  boostProficiency,
  COVER_COLORS,
  coverBg,
  deleteCourse,
  deleteChapter,
  getCourse,
  getSourceDocument,
  publishCourse,
  regenerateChapter,
  reorderChapters,
  statusPill,
  updateChapter,
  updateCourseMeta,
} from "@/lib/courses";
import { StringListEditor, HomeworkEditor, TestEditor } from "@/components/ChapterEditors";
import SchedulePanel from "@/components/SchedulePanel";
import CreateBookModal from "@/components/CreateBookModal";
import { openBrochurePdf } from "@/lib/payments";
import {
  booksStatus,
  generateBooks,
  listBooks,
  openChapterBookPdf,
  patchChapterBook,
} from "@/lib/books";
import {
  putCourseStudyPackSettings,
  openStudyPackPdf,
  adminGetStudyPackDefaults,
} from "@/lib/studyPack";
import InlineMarkdownEditor from "@/components/InlineMarkdownEditor";
import {
  BookOpenText,
  GripVertical,
  Loader2,
  Save,
  Plus,
  Trash2,
  Sparkles,
  Wand2,
  Target,
  Eye,
  Archive,
  Download,
  FileText,
  RefreshCw,
  Package,
} from "lucide-react";
import { toast } from "sonner";

const LEVELS = ["Beginner", "Intermediate", "Advanced"];

export default function CourseEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const asAdmin =
    currentUser?.role === "admin" &&
    (typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("as_admin") === "1" ||
        new URLSearchParams(window.location.search).has("as_admin")
      : false);

  const [course, setCourse] = useState(null);
  const [activeChapterId, setActiveChapterId] = useState(null);
  const [savingState, setSavingState] = useState("idle"); // idle | saving | saved | error
  const [busy, setBusy] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenInstructions, setRegenInstructions] = useState("");
  const [regenChapterIdx, setRegenChapterIdx] = useState(null);
  // Phase 22 Item 1 — "+ Add chapter (AI)"
  const [aiAppendOpen, setAiAppendOpen] = useState(false);
  const [aiAppendPrompt, setAiAppendPrompt] = useState("");
  const [aiAppendLevel, setAiAppendLevel] = useState("");
  const [aiAppendBusy, setAiAppendBusy] = useState(false);
  // Phase 22 Item 2 — "🎯 Boost proficiency"
  const [boostOpen, setBoostOpen] = useState(false);
  const [boostLevel, setBoostLevel] = useState("");
  const [boostMode, setBoostMode] = useState("upgrade");
  const [boostAddCount, setBoostAddCount] = useState(3);
  const [boostBusy, setBoostBusy] = useState(false);
  const [pendingJobKind, setPendingJobKind] = useState(null); // "chapter_append" | "boost_upgrade" | "boost_append" | null
  // Phase 23 — Source document drawer state.
  const [sourceDocView, setSourceDocView] = useState(null); // {index, meta}
  const [sourceDocText, setSourceDocText] = useState(null);
  const [sourceDocLoading, setSourceDocLoading] = useState(false);

  // ---- Phase 11: chapter book state ----
  const [bookModalOpen, setBookModalOpen] = useState(false);
  const [bookSubmitting, setBookSubmitting] = useState(false);
  const [bookStatus, setBookStatus] = useState(null); // {status, percent, model, books, ...}
  const [booksByChapter, setBooksByChapter] = useState({}); // chapter_id -> book row

  // ---- Phase 12: study pack defaults (global floor) ----
  const [studyPackDefaults, setStudyPackDefaults] = useState(null);

  useEffect(() => {
    adminGetStudyPackDefaults()
      .then(setStudyPackDefaults)
      .catch(() => setStudyPackDefaults(null));
  }, []);

  // refs for debounced save
  const metaTimer = useRef(null);
  const chapterTimer = useRef(null);
  const dirtyChapterId = useRef(null);

  // ---- load ----
  useEffect(() => {
    getCourse(id)
      .then((c) => {
        setCourse(c);
        const first = [...c.chapters].sort((a, b) => a.order - b.order)[0];
        setActiveChapterId(first?.id || null);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.detail === "not_course_owner"
            ? "You don't own this course"
            : "Failed to load course"
        );
        navigate("/teacher", { replace: true });
      });
  }, [id, navigate]);

  // ---- AI generation polling overlay ----
  const aiJob = course?.ai_generation || null;
  const aiInProgress =
    aiJob && (aiJob.status === "queued" || aiJob.status === "running");
  useEffect(() => {
    if (!aiInProgress) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const job = await aiGenerationStatus(id);
        if (cancelled) return;
        if (job.status === "done") {
          // Refetch full course so freshly-generated chapters appear.
          const fresh = await getCourse(id);
          if (cancelled) return;
          setCourse(fresh);
          const first = [...fresh.chapters].sort((a, b) => a.order - b.order)[0];
          setActiveChapterId(first?.id || null);
          if (job.kind === "chapter_append") {
            toast.success("New chapter added");
          } else if (job.kind === "boost_upgrade") {
            toast.success(`Course boosted to ${fresh.level}`);
          } else if (job.kind === "boost_append") {
            toast.success(`Course boosted to ${fresh.level} — new chapters added`);
          } else {
            toast.success(`AI drafted ${fresh.chapters.length} chapters`);
          }
          setPendingJobKind(null);
        } else if (job.status === "failed") {
          setCourse((c) => (c ? { ...c, ai_generation: job } : c));
          toast.error(`AI generation failed: ${job.error || "unknown"}`);
        } else {
          // still queued/running — keep the latest snapshot for the overlay
          setCourse((c) => (c ? { ...c, ai_generation: job } : c));
        }
      } catch (err) {
        if (!cancelled) {
          // network blip — keep polling
        }
      }
    };
    const interval = setInterval(tick, 2000);
    tick(); // immediate fetch on mount
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [aiInProgress, id]);

  // ---- Phase 11: Book generation polling ----
  const bookInProgress =
    bookStatus &&
    (bookStatus.status === "queued" || bookStatus.status === "running");

  // Fetch any existing books on load.
  useEffect(() => {
    if (!id) return;
    listBooks(id)
      .then((rows) => {
        const map = {};
        rows.forEach((b) => {
          map[b.chapter_id] = b;
        });
        setBooksByChapter(map);
      })
      .catch(() => {});
  }, [id]);

  // Poll status when a job is in flight.
  useEffect(() => {
    if (!bookInProgress) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await booksStatus(id);
        if (cancelled) return;
        setBookStatus(s);
        if (s.status === "done" || s.status === "failed") {
          // refresh full books
          const rows = await listBooks(id);
          if (cancelled) return;
          const map = {};
          rows.forEach((b) => {
            map[b.chapter_id] = b;
          });
          setBooksByChapter(map);
          if (s.status === "done") {
            toast.success(`Chapter books ready: ${s.chapters_done}/${s.chapters_total}`);
          } else {
            toast.error("Book generation finished with errors. Retry per chapter.");
          }
        }
      } catch (err) {
        /* keep polling */
      }
    };
    const interval = setInterval(tick, 2000);
    tick();
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [bookInProgress, id]);

  const submitBookGeneration = async ({
    model,
    extra_instructions,
    auto_share,
    regenerate_existing,
  }) => {
    setBookSubmitting(true);
    try {
      // Persist auto-share preference on the course (so seed default updates next time)
      try {
        await patchChapterBook; // noop reference to keep eslint happy; real put below
      } catch {} // eslint-disable-line no-empty
      const r = await generateBooks(id, {
        model,
        extra_instructions,
        regenerate_existing,
        current_year_context: 2026,
      });
      setBookStatus({
        job_id: r.job_id,
        course_id: r.course_id,
        status: r.status,
        chapters_total: r.chapters_total,
        chapters_done: 0,
        chapters_failed: 0,
        percent: 0,
        model: r.model,
        books: [],
      });
      // Optimistically queue the book rows in our map so per-chapter UI updates immediately
      setBooksByChapter((prev) => {
        const next = { ...prev };
        (course?.chapters || []).forEach((ch) => {
          next[ch.id] = {
            ...(next[ch.id] || {}),
            chapter_id: ch.id,
            chapter_title: ch.title,
            status: "queued",
            shared_with_students:
              next[ch.id]?.shared_with_students ?? auto_share,
            model_used: r.model,
          };
        });
        return next;
      });
      setBookModalOpen(false);
      toast.success(`Generation queued · ${r.model}`);
    } catch (err) {
      const d = err?.response?.data?.detail || "Failed";
      toast.error(typeof d === "string" ? d : "Failed");
    } finally {
      setBookSubmitting(false);
    }
  };

  const toggleShareBook = async (chapter_id, value) => {
    try {
      const updated = await patchChapterBook(id, chapter_id, {
        shared_with_students: value,
      });
      setBooksByChapter((prev) => ({ ...prev, [chapter_id]: updated }));
      toast.success(value ? "Shared with students" : "Hidden from students");
    } catch (err) {
      const d = err?.response?.data?.detail || "Failed";
      toast.error(typeof d === "string" ? d : "Failed");
    }
  };

  const regenerateOneBook = async (chapter_id) => {
    try {
      const r = await generateBooks(id, {
        model: booksByChapter[chapter_id]?.model_used || "gpt-4o-mini",
        regenerate_existing: true,
        current_year_context: 2026,
        chapter_ids: [chapter_id],
      });
      setBookStatus({
        ...r,
        chapters_done: 0,
        chapters_failed: 0,
        percent: 0,
        books: [],
      });
      setBooksByChapter((prev) => ({
        ...prev,
        [chapter_id]: { ...prev[chapter_id], status: "queued" },
      }));
      toast.success("Regenerating this chapter's book…");
    } catch (err) {
      const d = err?.response?.data?.detail || "Failed";
      toast.error(typeof d === "string" ? d : "Failed");
    }
  };

  // Phase 12 — Retry all failed chapters in one shot.
  const failedChapterIds = useMemo(
    () =>
      Object.values(booksByChapter || {})
        .filter((b) => b && b.status === "failed")
        .map((b) => b.chapter_id),
    [booksByChapter]
  );

  const retryFailedBooks = async () => {
    if (failedChapterIds.length === 0) return;
    try {
      const r = await generateBooks(id, {
        model:
          course?.book_settings?.default_model ||
          booksByChapter[failedChapterIds[0]]?.model_used ||
          "gpt-4o-mini",
        regenerate_existing: true,
        current_year_context: 2026,
        chapter_ids: failedChapterIds,
      });
      setBookStatus({
        ...r,
        chapters_done: 0,
        chapters_failed: 0,
        percent: 0,
        books: [],
      });
      setBooksByChapter((prev) => {
        const next = { ...prev };
        failedChapterIds.forEach((cid) => {
          next[cid] = { ...(next[cid] || {}), status: "queued", error: null };
        });
        return next;
      });
      toast.success(
        `Retrying ${failedChapterIds.length} failed chapter${
          failedChapterIds.length === 1 ? "" : "s"
        }…`
      );
    } catch (err) {
      const d = err?.response?.data?.detail || "Failed";
      toast.error(typeof d === "string" ? d : "Failed");
    }
  };

  // Phase 12 — Inline save for one markdown / list field on the book.
  const saveBookField = async (chapter_id, field, value) => {
    try {
      const updated = await patchChapterBook(id, chapter_id, {
        [field]: value,
      });
      setBooksByChapter((prev) => ({ ...prev, [chapter_id]: updated }));
    } catch (err) {
      const d = err?.response?.data?.detail || "Save failed";
      toast.error(typeof d === "string" ? d : "Save failed");
      throw err;
    }
  };

  // Phase 12 — Per-course Study Pack settings.
  const studyPack = course?.book_settings?.study_pack || {
    enabled: false,
    price_inr: 0,
    price_usd: 0,
  };

  const saveStudyPackSettings = async (patch) => {
    try {
      const r = await putCourseStudyPackSettings(id, patch);
      setCourse((c) =>
        c
          ? {
              ...c,
              book_settings: {
                ...(c.book_settings || {}),
                study_pack: r.study_pack,
              },
            }
          : c
      );
      toast.success("Study Pack updated");
    } catch (err) {
      const d = err?.response?.data?.detail || "Failed";
      toast.error(typeof d === "string" ? d : "Failed");
    }
  };

  const sortedChapters = useMemo(
    () => (course ? [...course.chapters].sort((a, b) => a.order - b.order) : []),
    [course]
  );
  const activeChapter = useMemo(
    () => sortedChapters.find((c) => c.id === activeChapterId) || null,
    [sortedChapters, activeChapterId]
  );

  // ---- meta save (debounced) ----
  const scheduleMetaSave = useCallback(
    (patch) => {
      if (!course) return;
      setCourse((c) => ({ ...c, ...patch }));
      setSavingState("saving");
      clearTimeout(metaTimer.current);
      metaTimer.current = setTimeout(async () => {
        try {
          const updated = await updateCourseMeta(course.id, patch);
          setCourse((prev) => ({ ...prev, ...updated, chapters: prev.chapters }));
          setSavingState("saved");
        } catch (err) {
          setSavingState("error");
          toast.error("Save failed");
        }
      }, 1500);
    },
    [course]
  );

  // ---- chapter local edit + debounced save ----
  const editActiveChapter = useCallback(
    (patch) => {
      if (!activeChapter) return;
      const merged = { ...activeChapter, ...patch };
      setCourse((c) => ({
        ...c,
        chapters: c.chapters.map((ch) => (ch.id === merged.id ? merged : ch)),
      }));
      dirtyChapterId.current = merged.id;
      setSavingState("saving");
      clearTimeout(chapterTimer.current);
      chapterTimer.current = setTimeout(async () => {
        try {
          const fresh = await updateChapter(course.id, merged.id, merged);
          setCourse(fresh);
          setSavingState("saved");
        } catch {
          setSavingState("error");
          toast.error("Chapter save failed");
        }
      }, 1500);
    },
    [activeChapter, course]
  );

  // ---- explicit save (button) ----
  const saveNow = async () => {
    if (!course || !activeChapter) return;
    setSavingState("saving");
    try {
      const fresh = await updateChapter(course.id, activeChapter.id, activeChapter);
      setCourse(fresh);
      setSavingState("saved");
      toast.success("Saved");
    } catch {
      setSavingState("error");
      toast.error("Save failed");
    }
  };

  // ---- chapter actions ----
  const onAddChapter = async () => {
    setBusy(true);
    try {
      const fresh = await addChapter(course.id, "Untitled chapter");
      setCourse(fresh);
      const newest = [...fresh.chapters].sort((a, b) => b.order - a.order)[0];
      setActiveChapterId(newest.id);
      toast.success("Chapter added");
    } finally {
      setBusy(false);
    }
  };

  // Phase 22 Item 1 — Kick off AI append.
  const onAiAppendSubmit = async () => {
    if (!aiAppendPrompt.trim()) {
      toast.error("Add a short prompt for the new chapter.");
      return;
    }
    setAiAppendBusy(true);
    try {
      const res = await aiAppendChapter(
        course.id,
        aiAppendPrompt.trim(),
        aiAppendLevel || undefined
      );
      // Refetch course so the placeholder appears immediately.
      const fresh = await getCourse(course.id);
      setCourse(fresh);
      setPendingJobKind("chapter_append");
      setAiAppendOpen(false);
      setAiAppendPrompt("");
      setAiAppendLevel("");
      toast.info("Drafting the new chapter with AI…");
      // ChapterId available in res.chapter_id if we want to focus later.
      setActiveChapterId(res.chapter_id);
    } catch (err) {
      const detail = err?.response?.data?.detail || "failed";
      toast.error(`Couldn't queue the append — ${detail}`);
    } finally {
      setAiAppendBusy(false);
    }
  };

  // Phase 22 Item 2 — Kick off Boost proficiency.
  const onBoostSubmit = async () => {
    if (!boostLevel) {
      toast.error("Pick a target level.");
      return;
    }
    setBoostBusy(true);
    try {
      await boostProficiency(course.id, {
        target_level: boostLevel,
        mode: boostMode,
        add_count: boostMode === "append" ? Number(boostAddCount) || 3 : undefined,
      });
      const fresh = await getCourse(course.id);
      setCourse(fresh);
      setPendingJobKind(`boost_${boostMode}`);
      setBoostOpen(false);
      setBoostLevel("");
      toast.info(
        boostMode === "upgrade"
          ? "Regenerating chapters at the new level…"
          : "Drafting new advanced chapters…"
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || "failed";
      if (detail === "target_level_not_higher") {
        toast.error("Target level must be higher than the current level.");
      } else {
        toast.error(`Boost failed — ${detail}`);
      }
    } finally {
      setBoostBusy(false);
    }
  };


  // Phase 23 — Load source doc text on demand for the drawer.
  useEffect(() => {
    if (!sourceDocView) {
      setSourceDocText(null);
      return;
    }
    let cancelled = false;
    setSourceDocLoading(true);
    getSourceDocument(id, sourceDocView.index)
      .then((row) => {
        if (!cancelled) setSourceDocText(row?.text || "");
      })
      .catch(() => {
        if (!cancelled) setSourceDocText("");
        toast.error("Couldn't load extracted text");
      })
      .finally(() => {
        if (!cancelled) setSourceDocLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sourceDocView, id]);


  const onDeleteChapter = async (chId) => {
    setBusy(true);
    try {
      const fresh = await deleteChapter(course.id, chId);
      setCourse(fresh);
      if (activeChapterId === chId) {
        const first = [...fresh.chapters].sort((a, b) => a.order - b.order)[0];
        setActiveChapterId(first?.id || null);
      }
      toast.success("Chapter deleted");
    } finally {
      setBusy(false);
    }
  };

  const onDragEnd = async (res) => {
    if (!res.destination || res.destination.index === res.source.index) return;
    const ids = sortedChapters.map((c) => c.id);
    const [moved] = ids.splice(res.source.index, 1);
    ids.splice(res.destination.index, 0, moved);
    setCourse((c) => ({
      ...c,
      chapters: ids.map((cid, i) => ({
        ...c.chapters.find((x) => x.id === cid),
        order: i,
      })),
    }));
    try {
      const fresh = await reorderChapters(course.id, ids);
      setCourse(fresh);
    } catch {
      toast.error("Reorder failed");
    }
  };

  const onRegenerate = async () => {
    if (regenChapterIdx == null || !regenInstructions.trim()) return;
    setBusy(true);
    setRegenOpen(false);
    toast.message("Regenerating chapter… (15–40s)");
    try {
      const fresh = await regenerateChapter(course.id, regenChapterIdx, regenInstructions);
      setCourse(fresh);
      toast.success("Chapter regenerated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Regen failed");
    } finally {
      setBusy(false);
      setRegenInstructions("");
      setRegenChapterIdx(null);
    }
  };

  // ---- publish / archive / delete ----
  const onPublish = async () => {
    setBusy(true);
    try {
      const fresh = await publishCourse(course.id);
      setCourse(fresh);
      toast.success("Course published");
    } catch (err) {
      const errs = err?.response?.data?.detail?.errors;
      if (Array.isArray(errs)) {
        toast.error("Cannot publish: " + errs.join("; "));
      } else {
        toast.error("Publish failed");
      }
    } finally {
      setBusy(false);
    }
  };
  const onRepublish = async () => {
    setBusy(true);
    try {
      const fresh = await republishCourse(course.id);
      setCourse(fresh);
      toast.success("Saved & republished");
    } catch (err) {
      const errs = err?.response?.data?.detail?.errors;
      if (Array.isArray(errs)) {
        toast.error("Cannot republish: " + errs.join("; "));
      } else {
        toast.error("Republish failed");
      }
    } finally {
      setBusy(false);
    }
  };
  const onUnpublish = async () => {
    setBusy(true);
    try {
      const fresh = await unpublishCourse(course.id);
      setCourse(fresh);
      toast.success("Course unpublished — removed from public catalog");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unpublish failed");
    } finally {
      setBusy(false);
    }
  };
  const onArchive = async () => {
    setBusy(true);
    try {
      const fresh = await archiveCourse(course.id);
      setCourse(fresh);
      toast.success("Course archived");
    } finally {
      setBusy(false);
    }
  };
  const onDelete = async () => {
    setBusy(true);
    try {
      await deleteCourse(course.id);
      toast.success("Course deleted");
      navigate("/teacher", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
      setBusy(false);
    }
  };

  if (!course) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-stone-700" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="course-editor">
      <DashboardHeader subtitle="Teacher · Course editor" />

      {/* AI generation overlay — shown while a background job is in-flight or just failed */}
      {aiJob && aiJob.status !== "done" && (
        <div
          className={`border-b ${
            aiJob.status === "failed"
              ? "border-rose-300 bg-rose-50 text-rose-900"
              : "border-amber-300 bg-amber-50 text-amber-900"
          }`}
          data-testid="ai-generation-overlay"
        >
          <div className="mx-auto max-w-7xl px-6 py-3 flex items-center gap-3 text-sm">
            {aiJob.status === "failed" ? (
              <>
                <span className="font-medium">AI generation failed.</span>
                <span className="text-rose-700" data-testid="ai-job-error">
                  {aiJob.error || "Please try again or contact support."}
                </span>
              </>
            ) : (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="font-medium">AI is drafting your chapters…</span>
                <span className="text-amber-700" data-testid="ai-job-progress">
                  {aiJob.chapters_done}/{aiJob.chapters_total} chapters
                  {" · "}
                  {aiJob.status}
                </span>
                <span className="text-amber-700">(~30–60s, you can keep this tab open)</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Phase 13 — Admin mode banner */}
      {asAdmin && (
        <div
          className="border-b border-amber-300 bg-amber-50 text-amber-900"
          data-testid="admin-edit-banner"
        >
          <div className="mx-auto max-w-7xl px-6 py-2 text-sm">
            <b>Admin mode</b> — editing on behalf of teacher{" "}
            <span className="font-mono">{course?.owner_teacher_id?.slice(0, 8) || "?"}…</span>.
            Changes are recorded in the course audit log.
          </div>
        </div>
      )}

      {/* Book generation overlay (Phase 11) */}
      {bookStatus && (bookStatus.status === "queued" || bookStatus.status === "running") && (
        <div
          className="border-b border-amber-300 bg-amber-50 text-amber-900"
          data-testid="book-generation-overlay"
        >
          <div className="mx-auto max-w-7xl px-6 py-3 flex items-center gap-3 text-sm">
            <BookOpenText className="h-4 w-4 animate-pulse" />
            <span className="font-medium">Generating chapter books…</span>
            <span className="text-amber-700" data-testid="book-progress">
              {bookStatus.chapters_done} of {bookStatus.chapters_total} done
              {bookStatus.chapters_failed > 0 ? ` · ${bookStatus.chapters_failed} failed` : ""}
              {" · Model: "}
              {bookStatus.model}
            </span>
            <span className="text-amber-700">({bookStatus.percent}%)</span>
          </div>
        </div>
      )}

      {/* Phase 12 — Retry failed chapters banner */}
      {failedChapterIds.length > 0 && !bookInProgress && (
        <div
          className="border-b border-rose-300 bg-rose-50 text-rose-900"
          data-testid="book-retry-banner"
        >
          <div className="mx-auto max-w-7xl px-6 py-3 flex items-center gap-3 text-sm flex-wrap">
            <RefreshCw className="h-4 w-4" />
            <span className="font-medium" data-testid="retry-failed-count">
              {failedChapterIds.length} chapter{failedChapterIds.length === 1 ? "" : "s"} failed
            </span>
            <span className="text-rose-700">
              You can retry just the failed ones without touching the rest.
            </span>
            <Button
              size="sm"
              onClick={retryFailedBooks}
              className="ml-auto bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="retry-failed-btn"
            >
              <RefreshCw className="mr-1 h-4 w-4" /> Retry {failedChapterIds.length} failed chapter
              {failedChapterIds.length === 1 ? "" : "s"}
            </Button>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="border-b border-stone-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-3 flex items-center gap-3 flex-wrap">
          <BackButton to="/teacher" className="mr-1" />
          <Input
            value={course.title}
            onChange={(e) => scheduleMetaSave({ title: e.target.value })}
            className="max-w-xl text-base font-medium"
            data-testid="course-title-input"
          />
          <Badge variant="outline" className={statusPill(course.status)} data-testid="course-status">
            {course.status}
          </Badge>
          <div className="text-xs text-stone-500" data-testid="save-state">
            {savingState === "saving" && (
              <span className="flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> saving…
              </span>
            )}
            {savingState === "saved" && "saved"}
            {savingState === "error" && <span className="text-red-600">error</span>}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {course.status === "published" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setBookModalOpen(true)}
                data-testid="create-book-btn"
                className="border-amber-300 text-amber-800 hover:bg-amber-50"
              >
                <BookOpenText className="mr-1 h-4 w-4" /> Create book
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={saveNow}
              data-testid="manual-save-button"
            >
              <Save className="mr-1 h-4 w-4" /> Save
            </Button>
            {course.status === "draft" && (
              <Button
                size="sm"
                onClick={onPublish}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={busy}
                data-testid="publish-button"
              >
                <Eye className="mr-1 h-4 w-4" /> Publish
              </Button>
            )}
            {course.status === "published" && (
              <Button
                size="sm"
                onClick={onRepublish}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={busy}
                data-testid="republish-button"
                title="Republishes after applying edits."
              >
                <Eye className="mr-1 h-4 w-4" /> Save & Republish
              </Button>
            )}
            {asAdmin && course.status === "published" && (
              <Button
                size="sm"
                variant="outline"
                onClick={onUnpublish}
                disabled={busy}
                className="border-amber-300 text-amber-800"
                data-testid="unpublish-button"
                title="Admin: pull this course out of the public catalog."
              >
                Unpublish
              </Button>
            )}
            {course.status !== "archived" && (
              <Button variant="outline" size="sm" onClick={onArchive} data-testid="archive-button">
                <Archive className="mr-1 h-4 w-4" /> Archive
              </Button>
            )}
            {course.status === "draft" && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-red-300 text-red-700"
                    data-testid="delete-course-button"
                  >
                    <Trash2 className="mr-1 h-4 w-4" /> Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this draft course?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={onDelete}
                      className="bg-red-600 hover:bg-red-700"
                      data-testid="delete-confirm-button"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-6 py-6 grid gap-6 lg:grid-cols-[280px_1fr]">
        {/* Sidebar */}
        <aside data-testid="chapter-sidebar">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold">Chapters</div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setAiAppendLevel(course?.level || "");
                  setAiAppendOpen(true);
                }}
                title="Draft a new chapter with AI"
                data-testid="ai-append-chapter-button"
              >
                <Wand2 className="h-3.5 w-3.5 text-amber-600 mr-1" />
                AI
              </Button>
              <Button size="sm" variant="outline" onClick={onAddChapter} data-testid="add-chapter-button">
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="chapters">
              {(prov) => (
                <div ref={prov.innerRef} {...prov.droppableProps} className="space-y-2">
                  {sortedChapters.map((ch, i) => (
                    <Draggable key={ch.id} draggableId={ch.id} index={i}>
                      {(p) => (
                        <div
                          ref={p.innerRef}
                          {...p.draggableProps}
                          className={`flex items-center gap-2 px-2 py-2 rounded-md border ${
                            activeChapterId === ch.id
                              ? "border-stone-900 bg-white"
                              : "border-stone-200 bg-white hover:border-stone-400"
                          }`}
                          data-testid={`chapter-item-${i}`}
                        >
                          <span
                            {...p.dragHandleProps}
                            className="text-stone-400 cursor-grab"
                            data-testid={`chapter-drag-${i}`}
                          >
                            <GripVertical className="h-4 w-4" />
                          </span>
                          <button
                            type="button"
                            className="flex-1 text-left text-sm truncate"
                            onClick={() => setActiveChapterId(ch.id)}
                            data-testid={`chapter-select-${i}`}
                          >
                            <span className="text-stone-400 mr-1">{i + 1}.</span> {ch.title}
                          </button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              setRegenChapterIdx(i);
                              setRegenOpen(true);
                            }}
                            title="Regenerate with AI"
                            data-testid={`chapter-regen-${i}`}
                          >
                            <Sparkles className="h-3.5 w-3.5 text-amber-600" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost" data-testid={`chapter-delete-${i}`}>
                                <Trash2 className="h-3.5 w-3.5 text-stone-500" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete chapter "{ch.title}"?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => onDeleteChapter(ch.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                  data-testid={`chapter-delete-confirm-${i}`}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {prov.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </aside>

        {/* Right pane: tabs */}
        <section data-testid="editor-pane">
          <Tabs defaultValue="overview">
            <TabsList>
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="classwork" data-testid="tab-classwork" disabled={!activeChapter}>
                Classwork
              </TabsTrigger>
              <TabsTrigger value="homework" data-testid="tab-homework" disabled={!activeChapter}>
                Homework
              </TabsTrigger>
              <TabsTrigger value="test" data-testid="tab-test" disabled={!activeChapter}>
                Test
              </TabsTrigger>
              <TabsTrigger value="schedule" data-testid="tab-schedule">
                Schedule
              </TabsTrigger>
            </TabsList>

            {/* Overview = course-level metadata + pricing */}
            <TabsContent value="overview">
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="text-base">Course details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Subtitle</Label>
                    <Input
                      value={course.subtitle || ""}
                      onChange={(e) => scheduleMetaSave({ subtitle: e.target.value })}
                      data-testid="course-subtitle"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      rows={4}
                      value={course.description || ""}
                      onChange={(e) => scheduleMetaSave({ description: e.target.value })}
                      data-testid="course-description"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Level</Label>
                      <div className="flex gap-2 items-center">
                        <Select
                          value={course.level}
                          onValueChange={(v) => scheduleMetaSave({ level: v })}
                        >
                          <SelectTrigger data-testid="course-level-select" className="flex-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LEVELS.map((l) => (
                              <SelectItem key={l} value={l}>
                                {l}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={course.level === "Advanced"}
                          onClick={() => {
                            const nextLevel =
                              course.level === "Beginner"
                                ? "Intermediate"
                                : "Advanced";
                            setBoostLevel(nextLevel);
                            setBoostMode("upgrade");
                            setBoostAddCount(3);
                            setBoostOpen(true);
                          }}
                          title="Regenerate at a higher level"
                          data-testid="boost-proficiency-button"
                        >
                          <Target className="h-3.5 w-3.5 mr-1 text-amber-600" />
                          Boost
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Cover color</Label>
                      <div className="flex gap-2 flex-wrap">
                        {COVER_COLORS.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => scheduleMetaSave({ cover_color: c })}
                            className={`h-8 w-8 rounded border-2 ${coverBg(c)} ${
                              course.cover_color === c
                                ? "ring-2 ring-stone-900"
                                : ""
                            }`}
                            aria-label={c}
                            data-testid={`cover-color-${c}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-stone-200 mt-4" data-testid="pricing-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-base">Pricing & plans</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      openBrochurePdf(course.id).catch((e) =>
                        toast.error(
                          e?.message || "Failed to generate brochure"
                        )
                      )
                    }
                    data-testid="preview-brochure-button"
                  >
                    <FileText className="mr-1 h-4 w-4" /> Preview brochure PDF
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label>Primary currency</Label>
                      <Select
                        value={course.pricing.currency_primary}
                        onValueChange={(v) =>
                          scheduleMetaSave({
                            pricing: { ...course.pricing, currency_primary: v },
                          })
                        }
                      >
                        <SelectTrigger data-testid="currency-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="INR">INR ₹</SelectItem>
                          <SelectItem value="USD">USD $</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Amount (₹)</Label>
                      <Input
                        type="number"
                        min={0}
                        value={course.pricing.amount_inr ?? ""}
                        onChange={(e) =>
                          scheduleMetaSave({
                            pricing: {
                              ...course.pricing,
                              amount_inr: e.target.value === "" ? null : Number(e.target.value),
                            },
                          })
                        }
                        data-testid="price-inr"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Amount ($)</Label>
                      <Input
                        type="number"
                        min={0}
                        value={course.pricing.amount_usd ?? ""}
                        onChange={(e) =>
                          scheduleMetaSave({
                            pricing: {
                              ...course.pricing,
                              amount_usd: e.target.value === "" ? null : Number(e.target.value),
                            },
                          })
                        }
                        data-testid="price-usd"
                      />
                    </div>
                  </div>

                  {/* plans */}
                  {[
                    { key: "pay_in_full", label: "Pay in full" },
                    { key: "part_payment", label: "Part payment" },
                    { key: "monthly", label: "Monthly subscription" },
                  ].map((plan) => (
                    <div
                      key={plan.key}
                      className="flex items-center justify-between border border-stone-200 rounded-md px-3 py-2"
                    >
                      <div>
                        <div className="text-sm font-medium">{plan.label}</div>
                        {plan.key === "pay_in_full" && (
                          <div className="text-xs text-stone-500">
                            Discount when paid at once
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {plan.key === "pay_in_full" && (
                          <div className="flex items-center gap-2 text-sm">
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              className="w-20"
                              value={course.pricing.plans.pay_in_full.discount_percent}
                              onChange={(e) =>
                                scheduleMetaSave({
                                  pricing: {
                                    ...course.pricing,
                                    plans: {
                                      ...course.pricing.plans,
                                      pay_in_full: {
                                        ...course.pricing.plans.pay_in_full,
                                        discount_percent: Number(e.target.value) || 0,
                                      },
                                    },
                                  },
                                })
                              }
                              data-testid="plan-pay-discount"
                            />
                            <span className="text-xs text-stone-500">%</span>
                          </div>
                        )}
                        {plan.key === "part_payment" && (
                          <div className="flex items-center gap-2 text-sm">
                            <Input
                              type="number"
                              min={2}
                              max={12}
                              className="w-20"
                              value={course.pricing.plans.part_payment.num_parts}
                              onChange={(e) =>
                                scheduleMetaSave({
                                  pricing: {
                                    ...course.pricing,
                                    plans: {
                                      ...course.pricing.plans,
                                      part_payment: {
                                        ...course.pricing.plans.part_payment,
                                        num_parts: Number(e.target.value) || 2,
                                      },
                                    },
                                  },
                                })
                              }
                              data-testid="plan-parts-num"
                            />
                            <span className="text-xs text-stone-500">parts</span>
                          </div>
                        )}
                        {plan.key === "monthly" && (
                          <div className="flex items-center gap-2 text-sm">
                            <Input
                              type="number"
                              min={2}
                              max={24}
                              className="w-20"
                              value={course.pricing.plans.monthly.num_months}
                              onChange={(e) =>
                                scheduleMetaSave({
                                  pricing: {
                                    ...course.pricing,
                                    plans: {
                                      ...course.pricing.plans,
                                      monthly: {
                                        ...course.pricing.plans.monthly,
                                        num_months: Number(e.target.value) || 3,
                                      },
                                    },
                                  },
                                })
                              }
                              data-testid="plan-monthly-num"
                            />
                            <span className="text-xs text-stone-500">months</span>
                          </div>
                        )}
                        <Switch
                          checked={course.pricing.plans[plan.key].enabled}
                          onCheckedChange={(v) =>
                            scheduleMetaSave({
                              pricing: {
                                ...course.pricing,
                                plans: {
                                  ...course.pricing.plans,
                                  [plan.key]: {
                                    ...course.pricing.plans[plan.key],
                                    enabled: v,
                                  },
                                },
                              },
                            })
                          }
                          data-testid={`plan-${plan.key.split("_").join("-")}-toggle`}
                        />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Phase 22 — Item 3. Free access before payment toggle. */}
              <Card className="border-stone-200 mt-4" data-testid="free-access-card">
                <CardContent className="py-4 flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium">
                      Allow students to access content before payment
                    </div>
                    <div className="text-xs text-stone-500 mt-1 max-w-md">
                      When on, students who are enrolled but haven&apos;t paid
                      yet can still read chapters, do homework and take tests.
                      They&apos;ll see a &quot;pay to continue&quot; banner and
                      can complete payment any time.
                    </div>
                  </div>
                  <Switch
                    checked={!!course.pricing.free_access_before_payment}
                    onCheckedChange={(v) =>
                      scheduleMetaSave({
                        pricing: {
                          ...course.pricing,
                          free_access_before_payment: v,
                        },
                      })
                    }
                    data-testid="free-access-toggle"
                  />
                </CardContent>
              </Card>

              {/* Phase 23 — Uploaded reference documents (read-only) */}
              {(course?.source_documents?.length || 0) > 0 && (
                <Card
                  className="border-stone-200 mt-4"
                  data-testid="source-documents-card"
                >
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="h-4 w-4 text-amber-600" />
                      Reference documents ({course.source_documents.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-xs text-stone-500">
                      These PDFs/DOCX files were used when the AI drafted this
                      course. They&apos;re preserved so regenerations can reuse
                      the same context.
                    </p>
                    <ul className="space-y-1.5">
                      {course.source_documents.map((doc, i) => (
                        <li
                          key={`${doc.filename}-${i}`}
                          className="flex items-center justify-between rounded border border-stone-200 px-3 py-2 text-xs"
                          data-testid={`source-doc-row-${i}`}
                        >
                          <div className="min-w-0 pr-3">
                            <div className="font-medium truncate">
                              {doc.filename}
                            </div>
                            <div className="text-stone-500 flex items-center gap-2">
                              <span>{doc.char_count.toLocaleString()} chars</span>
                              <span>·</span>
                              <span>
                                {doc.uploaded_at
                                  ? new Date(doc.uploaded_at).toLocaleDateString(
                                      undefined,
                                      { day: "numeric", month: "short" }
                                    )
                                  : "—"}
                              </span>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              setSourceDocView({ index: i, meta: doc })
                            }
                            data-testid={`source-doc-view-${i}`}
                          >
                            View extracted text
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}


              {/* Phase 12 — Study Pack pricing */}
              <Card className="border-stone-200 mt-4" data-testid="study-pack-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Package className="h-4 w-4 text-amber-600" /> Study Pack (bundled PDF)
                  </CardTitle>
                  <div className="flex items-center gap-2 text-xs text-stone-500">
                    {studyPack.enabled ? "Selling" : "Disabled"}
                    <Switch
                      checked={!!studyPack.enabled}
                      onCheckedChange={(v) =>
                        saveStudyPackSettings({ enabled: v })
                      }
                      data-testid="study-pack-toggle"
                    />
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-stone-500">
                    A bundled PDF of every shared chapter's reference book.
                    Sold per-course as a one-time upsell to paying students.
                    Teacher delivery notes are never included.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Price (₹)</Label>
                      <Input
                        type="number"
                        min={studyPackDefaults?.min_price_inr ?? 0}
                        value={studyPack.price_inr ?? 0}
                        onChange={(e) => {
                          const v = Number(e.target.value) || 0;
                          setCourse((c) =>
                            c
                              ? {
                                  ...c,
                                  book_settings: {
                                    ...(c.book_settings || {}),
                                    study_pack: { ...studyPack, price_inr: v },
                                  },
                                }
                              : c
                          );
                        }}
                        onBlur={(e) => {
                          const v = Number(e.target.value) || 0;
                          const floor = studyPackDefaults?.min_price_inr ?? 0;
                          if (v < floor) {
                            toast.error(
                              `INR price must be ≥ ${floor} (admin floor)`
                            );
                            return;
                          }
                          saveStudyPackSettings({ price_inr: v });
                        }}
                        data-testid="study-pack-price-inr"
                      />
                      <div className="text-[11px] text-stone-500">
                        Floor ₹{studyPackDefaults?.min_price_inr ?? 0} · Default ₹
                        {studyPackDefaults?.default_price_inr ?? 99}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label>Price ($)</Label>
                      <Input
                        type="number"
                        min={studyPackDefaults?.min_price_usd ?? 0}
                        step="0.01"
                        value={studyPack.price_usd ?? 0}
                        onChange={(e) => {
                          const v = Number(e.target.value) || 0;
                          setCourse((c) =>
                            c
                              ? {
                                  ...c,
                                  book_settings: {
                                    ...(c.book_settings || {}),
                                    study_pack: { ...studyPack, price_usd: v },
                                  },
                                }
                              : c
                          );
                        }}
                        onBlur={(e) => {
                          const v = Number(e.target.value) || 0;
                          const floor = studyPackDefaults?.min_price_usd ?? 0;
                          if (v < floor) {
                            toast.error(
                              `USD price must be ≥ $${floor} (admin floor)`
                            );
                            return;
                          }
                          saveStudyPackSettings({ price_usd: v });
                        }}
                        data-testid="study-pack-price-usd"
                      />
                      <div className="text-[11px] text-stone-500">
                        Floor ${studyPackDefaults?.min_price_usd ?? 0} · Default $
                        {studyPackDefaults?.default_price_usd ?? 2}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      openStudyPackPdf(course.id).catch((e) =>
                        toast.error(e?.message || "Failed to preview Study Pack")
                      )
                    }
                    data-testid="study-pack-preview-pdf"
                  >
                    <FileText className="mr-1 h-4 w-4" /> Preview Study Pack PDF
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="classwork">
              {activeChapter && (
                <Card className="border-stone-200" data-testid="classwork-card">
                  <CardHeader>
                    <CardTitle className="text-base">Classwork</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Chapter title</Label>
                      <Input
                        value={activeChapter.title}
                        onChange={(e) => editActiveChapter({ title: e.target.value })}
                        data-testid="chapter-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Learning objectives</Label>
                      <StringListEditor
                        items={activeChapter.learning_objectives}
                        onChange={(v) => editActiveChapter({ learning_objectives: v })}
                        placeholder="Students will be able to…"
                        testidPrefix="obj"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Lesson notes (markdown)</Label>
                      <Textarea
                        rows={10}
                        value={activeChapter.classwork.lesson_notes_markdown}
                        onChange={(e) =>
                          editActiveChapter({
                            classwork: {
                              ...activeChapter.classwork,
                              lesson_notes_markdown: e.target.value,
                            },
                          })
                        }
                        data-testid="lesson-notes"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Examples</Label>
                      <StringListEditor
                        items={activeChapter.classwork.examples}
                        onChange={(v) =>
                          editActiveChapter({
                            classwork: { ...activeChapter.classwork, examples: v },
                          })
                        }
                        placeholder="A concrete example"
                        testidPrefix="example"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Teacher talking points</Label>
                      <StringListEditor
                        items={activeChapter.classwork.teacher_talking_points}
                        onChange={(v) =>
                          editActiveChapter({
                            classwork: {
                              ...activeChapter.classwork,
                              teacher_talking_points: v,
                            },
                          })
                        }
                        placeholder="Point to mention"
                        testidPrefix="talking"
                      />
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Homework */}
            <TabsContent value="homework">
              {activeChapter && (
                <Card className="border-stone-200" data-testid="homework-card">
                  <CardHeader>
                    <CardTitle className="text-base">Homework</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <HomeworkEditor
                      homework={activeChapter.homework}
                      onChange={(v) => editActiveChapter({ homework: v })}
                    />
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Schedule */}
            <TabsContent value="schedule">
              <SchedulePanel course={course} />
            </TabsContent>

            {/* Test */}
            <TabsContent value="test">
              {activeChapter && (
                <Card className="border-stone-200" data-testid="test-card">
                  <CardHeader>
                    <CardTitle className="text-base">Test</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <TestEditor
                      test={activeChapter.test}
                      onChange={(v) => editActiveChapter({ test: v })}
                    />
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>

          {/* Reference Book card (Phase 11) */}
          {activeChapter && (
            <Card className="mt-4 border-amber-200" data-testid="book-card">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <BookOpenText className="h-4 w-4 text-amber-600" /> Reference Book
                  </CardTitle>
                  <div className="flex items-center gap-3">
                    {booksByChapter[activeChapter.id]?.status === "done" && (
                      <>
                        <label className="flex items-center gap-2 text-xs text-stone-600">
                          Share with students
                          <Switch
                            data-testid={`book-share-toggle-${activeChapter.id}`}
                            checked={!!booksByChapter[activeChapter.id]?.shared_with_students}
                            onCheckedChange={(v) => toggleShareBook(activeChapter.id, v)}
                          />
                        </label>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openChapterBookPdf(id, activeChapter.id)}
                          data-testid="book-pdf-btn"
                        >
                          <Download className="mr-1 h-4 w-4" /> PDF
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => regenerateOneBook(activeChapter.id)}
                          data-testid="book-regenerate-btn"
                          disabled={bookInProgress}
                        >
                          <RefreshCw className="mr-1 h-4 w-4" /> Regenerate
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!booksByChapter[activeChapter.id] && (
                  <p className="text-sm text-stone-500">
                    No book yet for this chapter. Click <b>Create book</b> in the
                    top bar to generate one for every chapter, or use{" "}
                    <b>Regenerate</b> after the first run to refresh this one.
                  </p>
                )}
                {booksByChapter[activeChapter.id]?.status === "queued" && (
                  <p className="text-sm text-amber-700">Queued…</p>
                )}
                {booksByChapter[activeChapter.id]?.status === "running" && (
                  <p className="text-sm text-amber-700 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Generating…
                  </p>
                )}
                {booksByChapter[activeChapter.id]?.status === "failed" && (
                  <div className="text-sm text-rose-700 flex items-center gap-3 flex-wrap" data-testid="chapter-book-failed">
                    <span>
                      Failed: {booksByChapter[activeChapter.id]?.error || "unknown"}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => regenerateOneBook(activeChapter.id)}
                      data-testid="chapter-retry-link"
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                    >
                      <RefreshCw className="mr-1 h-3.5 w-3.5" /> Retry this chapter
                    </Button>
                  </div>
                )}
                {booksByChapter[activeChapter.id]?.status === "done" && (
                  <div className="space-y-4 text-sm">
                    <div className="text-xs text-stone-500">
                      Generated with {booksByChapter[activeChapter.id].model_used}
                      {booksByChapter[activeChapter.id].generation_ms
                        ? ` · ${booksByChapter[activeChapter.id].generation_ms} ms`
                        : ""}
                    </div>
                    {[
                      ["Theory", "theory_markdown", "theory"],
                      ["Practical", "practical_markdown", "practical"],
                      ["Teacher delivery notes", "teacher_delivery_notes_markdown", "teacher-notes"],
                      ["Student notes", "student_notes_markdown", "student-notes"],
                    ].map(([label, field, slug]) => (
                      <InlineMarkdownEditor
                        key={field}
                        label={label}
                        value={booksByChapter[activeChapter.id][field] || ""}
                        onSave={(val) =>
                          saveBookField(activeChapter.id, field, val)
                        }
                        testid={`book-${slug}`}
                      />
                    ))}
                    <InlineMarkdownEditor
                      label={`Glossary (${
                        booksByChapter[activeChapter.id].glossary?.length || 0
                      } items, JSON)`}
                      value={JSON.stringify(
                        booksByChapter[activeChapter.id].glossary || [],
                        null,
                        2
                      )}
                      onSave={async (val) => {
                        let parsed;
                        try {
                          parsed = JSON.parse(val || "[]");
                          if (!Array.isArray(parsed)) throw new Error("not array");
                        } catch (e) {
                          toast.error("Glossary must be valid JSON array");
                          throw e;
                        }
                        await saveBookField(activeChapter.id, "glossary", parsed);
                      }}
                      testid="book-glossary"
                      rows={8}
                    />
                    <InlineMarkdownEditor
                      label={`Further reading (${
                        booksByChapter[activeChapter.id].further_reading?.length || 0
                      } items, JSON)`}
                      value={JSON.stringify(
                        booksByChapter[activeChapter.id].further_reading || [],
                        null,
                        2
                      )}
                      onSave={async (val) => {
                        let parsed;
                        try {
                          parsed = JSON.parse(val || "[]");
                          if (!Array.isArray(parsed)) throw new Error("not array");
                        } catch (e) {
                          toast.error("Further reading must be valid JSON array");
                          throw e;
                        }
                        await saveBookField(
                          activeChapter.id,
                          "further_reading",
                          parsed
                        );
                      }}
                      testid="book-further-reading"
                      rows={8}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </section>
      </main>

      {/* Phase 11 modal */}
      <CreateBookModal
        open={bookModalOpen}
        onOpenChange={setBookModalOpen}
        chaptersCount={(course?.chapters || []).length}
        defaultModel={course?.book_settings?.default_model || "gpt-4o-mini"}
        defaultAutoShare={course?.book_settings?.auto_share_with_students !== false}
        onSubmit={submitBookGeneration}
        submitting={bookSubmitting}
      />

      {/* Regen dialog */}
      <Dialog open={regenOpen} onOpenChange={setRegenOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate chapter with AI</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Revision instructions</Label>
            <Textarea
              rows={4}
              value={regenInstructions}
              onChange={(e) => setRegenInstructions(e.target.value)}
              placeholder="e.g. Add an India-centric example and a harder MCQ"
              data-testid="regen-instructions"
            />
            <p className="text-xs text-stone-500">
              The chapter's title, content, homework and test will all be rewritten
              based on these instructions. Takes 15–40s.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegenOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={onRegenerate}
              className="bg-stone-900 hover:bg-stone-800 text-white"
              data-testid="regen-confirm-button"
            >
              <Sparkles className="mr-1 h-4 w-4" /> Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 22 Item 1 — AI append chapter dialog */}
      <Dialog open={aiAppendOpen} onOpenChange={setAiAppendOpen}>
        <DialogContent data-testid="ai-append-dialog">
          <DialogHeader>
            <DialogTitle>Add a chapter with AI</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>What should this new chapter cover?</Label>
              <Textarea
                rows={4}
                value={aiAppendPrompt}
                onChange={(e) => setAiAppendPrompt(e.target.value)}
                placeholder="e.g. Introduce quadratic equations with real-world Indian examples"
                data-testid="ai-append-prompt"
              />
            </div>
            <div className="space-y-1">
              <Label>Target level</Label>
              <Select value={aiAppendLevel || course?.level} onValueChange={setAiAppendLevel}>
                <SelectTrigger data-testid="ai-append-level">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.map((l) => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-stone-500">
                Defaults to the current course level. Set higher if you&apos;re
                adding an advanced chapter to a beginner course.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiAppendOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={onAiAppendSubmit}
              className="bg-stone-900 hover:bg-stone-800 text-white"
              disabled={aiAppendBusy || !aiAppendPrompt.trim()}
              data-testid="ai-append-submit"
            >
              {aiAppendBusy ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="mr-1 h-4 w-4" />
              )}
              Draft chapter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 22 Item 2 — Boost proficiency dialog */}
      <Dialog open={boostOpen} onOpenChange={setBoostOpen}>
        <DialogContent data-testid="boost-dialog">
          <DialogHeader>
            <DialogTitle>Boost proficiency level</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-stone-500">Current level:</span>
              <Badge variant="outline" className="uppercase" data-testid="boost-current-level">
                {course?.level}
              </Badge>
            </div>
            <div className="space-y-1">
              <Label>Target level</Label>
              <Select value={boostLevel} onValueChange={setBoostLevel}>
                <SelectTrigger data-testid="boost-target-level">
                  <SelectValue placeholder="Pick a higher level" />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.filter(
                    (l) =>
                      ["Beginner", "Intermediate", "Advanced"].indexOf(l) >
                      ["Beginner", "Intermediate", "Advanced"].indexOf(course?.level)
                  ).map((l) => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>How to boost</Label>
              <label
                className="flex items-start gap-2 rounded border border-stone-200 p-2 text-sm cursor-pointer"
                data-testid="boost-mode-upgrade"
              >
                <input
                  type="radio"
                  name="boost-mode"
                  className="mt-1"
                  checked={boostMode === "upgrade"}
                  onChange={() => setBoostMode("upgrade")}
                />
                <div>
                  <div className="font-medium">Upgrade existing chapters</div>
                  <div className="text-xs text-stone-500">
                    Regenerate every chapter at the target level. Titles and
                    order are preserved.
                  </div>
                </div>
              </label>
              <label
                className="flex items-start gap-2 rounded border border-stone-200 p-2 text-sm cursor-pointer"
                data-testid="boost-mode-append"
              >
                <input
                  type="radio"
                  name="boost-mode"
                  className="mt-1"
                  checked={boostMode === "append"}
                  onChange={() => setBoostMode("append")}
                />
                <div className="flex-1">
                  <div className="font-medium">Add advanced chapters</div>
                  <div className="text-xs text-stone-500">
                    Keep existing chapters. Append N new chapters at the
                    target level.
                  </div>
                  {boostMode === "append" && (
                    <div className="mt-2 flex items-center gap-2">
                      <Label className="text-xs">How many?</Label>
                      <Input
                        type="number"
                        min={1}
                        max={5}
                        value={boostAddCount}
                        onChange={(e) =>
                          setBoostAddCount(Number(e.target.value) || 1)
                        }
                        className="w-16 h-8"
                        data-testid="boost-add-count"
                      />
                    </div>
                  )}
                </div>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBoostOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={onBoostSubmit}
              className="bg-stone-900 hover:bg-stone-800 text-white"
              disabled={boostBusy || !boostLevel}
              data-testid="boost-submit"
            >
              {boostBusy ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Target className="mr-1 h-4 w-4" />
              )}
              Boost
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 23 — Extracted source document drawer */}
      <Sheet
        open={!!sourceDocView}
        onOpenChange={(o) => (!o ? setSourceDocView(null) : null)}
      >
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg overflow-y-auto"
          data-testid="source-doc-drawer"
        >
          <SheetHeader>
            <SheetTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-amber-600" />
              {sourceDocView?.meta?.filename || "Source document"}
            </SheetTitle>
            <SheetDescription>
              {sourceDocView?.meta?.char_count?.toLocaleString?.() || 0} characters
              extracted · read-only
            </SheetDescription>
          </SheetHeader>
          {sourceDocLoading ? (
            <div className="py-6 flex justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-stone-500" />
            </div>
          ) : (
            <pre
              className="mt-4 whitespace-pre-wrap text-xs text-stone-700 bg-stone-50 border border-stone-200 rounded p-3 max-h-[70vh] overflow-y-auto"
              data-testid="source-doc-text"
            >
              {sourceDocText || "(no text extracted)"}
            </pre>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
